import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  getToken, clearToken, adminCreateProduct, adminUpdateProduct, adminDeleteProduct,
  adminListOrders, adminApi, adminUpdateOrderStatus, adminUpdateCategory, adminUpdateHeroImages,
  adminCreateShipment, adminSyncShipment, adminDeleteOrder,
  adminListReviews, adminCreateReview, adminUpdateReview, adminDeleteReview
} from "@/lib/adminApi";
import { getProducts, getCategories, getHeroImages } from "@/lib/api";
import {
  LogOut, Plus, Edit2, Trash2, Package, ShoppingBag, X, Upload, Grid, Image as ImageIcon, Home, Star
} from "lucide-react";
import { toast } from "sonner";
import { SmartImage } from "@/components/SmartImage";

const EMPTY = {
  name: "", slug: "", category: "roses", price: 0, original_price: 0, discount_percent: 0,
  short_desc: "", description: "", images: [], tags: [], stock: 100, is_featured: false,
};

const STATUS_META = {
  pending_whatsapp: { label: "Pending", color: "bg-amber-100 text-amber-700" },
  accepted: { label: "Accepted", color: "bg-blue-100 text-blue-700" },
  shipped: { label: "Shipped", color: "bg-purple-100 text-purple-700" },
  delivered: { label: "Delivered", color: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700" },
};

const catLabel = (c) => c === "valentine" ? "Valentine's" : c === "pots" ? "Flower Pots" : c[0].toUpperCase() + c.slice(1);

export const AdminDashboardPage = () => {
  const nav = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({ name: "", image: "", coming_soon: false });
  const [catUploading, setCatUploading] = useState(false);
  const [heroImages, setHeroImages] = useState([]);
  const [heroUploading, setHeroUploading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [editingReview, setEditingReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 5, text: "", approved: true });
  const fileRef = useRef(null);
  const catFileRef = useRef(null);
  const heroFileRef = useRef(null);

  useEffect(() => {
    if (!getToken()) { nav("/admin/login"); return; }
    refresh();
  }, []);

  const refresh = async () => {
    try {
      const [p, o, c, h, r] = await Promise.all([
        getProducts(),
        adminListOrders().catch(() => []),
        getCategories(),
        getHeroImages().catch(() => ({ images: [] })),
        adminListReviews().catch(() => []),
      ]);
      setProducts(p); setOrders(o); setCategories(c); setHeroImages(h.images || []); setReviews(r);
    } catch { toast.error("Failed to load data"); }
  };

  const openReviewNew = () => { setEditingReview({}); setReviewForm({ name: "", rating: 5, text: "", approved: true }); };
  const openReviewEdit = (rv) => { setEditingReview(rv); setReviewForm({ name: rv.name, rating: rv.rating, text: rv.text, approved: !!rv.approved }); };
  const saveReview = async () => {
    if (!reviewForm.name || !reviewForm.text) { toast.error("Name and text required"); return; }
    try {
      if (editingReview?.id) await adminUpdateReview(editingReview.id, reviewForm);
      else await adminCreateReview(reviewForm);
      toast.success("Review saved");
      setEditingReview(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const removeReview = async (rv) => {
    if (!window.confirm("Delete this review?")) return;
    try { await adminDeleteReview(rv.id); toast.success("Deleted"); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  const logout = () => { clearToken(); nav("/admin/login"); };

  // Product modal
  const openNew = (cat) => { setEditing({}); setForm({ ...EMPTY, category: cat || "roses" }); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...EMPTY, ...p,
      images: p.images?.length ? p.images : [],
      original_price: p.original_price ?? 0,
      discount_percent: p.discount_percent ?? 0,
    });
  };
  const save = async () => {
    if (!form.name || !form.slug || !form.price) { toast.error("Name, slug and price are required"); return; }
    const payload = {
      ...form,
      price: Number(form.price),
      original_price: Number(form.original_price) || null,
      discount_percent: Number(form.discount_percent) || 0,
      stock: Number(form.stock) || 0,
      images: form.images.filter(Boolean),
      tags: typeof form.tags === "string" ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : form.tags,
    };
    try {
      if (editing?.id) await adminUpdateProduct(editing.id, payload);
      else await adminCreateProduct(payload);
      toast.success("Saved");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await adminDeleteProduct(p.id); toast.success("Deleted"); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  // Product image upload (multiple)
  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const res = await adminApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const path = res.data.url; // relative /api/files/... — resolves against current origin
      setForm(f => ({ ...f, images: [...f.images, path] }));
      toast.success("Image added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const addImageUrl = () => {
    const url = window.prompt("Paste image URL");
    if (url) setForm(f => ({ ...f, images: [...f.images, url.trim()] }));
  };
  const removeImage = (i) => setForm(f => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  // Category editing
  const openCatEdit = (cat) => {
    setEditingCat(cat);
    setCatForm({ name: cat.name, image: cat.image, coming_soon: !!cat.coming_soon });
  };
  const saveCat = async () => {
    if (!catForm.name || !catForm.image) { toast.error("Name and image required"); return; }
    try {
      await adminUpdateCategory(editingCat.id, { ...catForm, order: editingCat.order || 0 });
      toast.success("Category updated");
      setEditingCat(null); refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const uploadCatImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setCatUploading(true);
    try {
      const res = await adminApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const path = res.data.url; // relative /api/files/...
      setCatForm(f => ({ ...f, image: path }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setCatUploading(false); if (catFileRef.current) catFileRef.current.value = ""; }
  };

  // Hero images
  const uploadHeroImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setHeroUploading(true);
    try {
      const res = await adminApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const path = res.data.url; // relative /api/files/...
      const next = [...heroImages, path];
      setHeroImages(next);
      await adminUpdateHeroImages(next);
      toast.success("Hero image added");
    } catch { toast.error("Upload failed"); }
    finally { setHeroUploading(false); if (heroFileRef.current) heroFileRef.current.value = ""; }
  };
  const addHeroUrl = async () => {
    const url = window.prompt("Paste hero image URL");
    if (!url) return;
    const next = [...heroImages, url.trim()];
    setHeroImages(next);
    try { await adminUpdateHeroImages(next); toast.success("Added"); } catch { toast.error("Save failed"); }
  };
  const removeHero = async (i) => {
    const next = heroImages.filter((_, idx) => idx !== i);
    setHeroImages(next);
    try { await adminUpdateHeroImages(next); toast.success("Removed"); } catch { toast.error("Save failed"); }
  };

  const [quickUploadId, setQuickUploadId] = useState(null);
  const quickFileRef = useRef(null);

  const quickChangeCover = (product) => {
    setQuickUploadId(product.id);
    setTimeout(() => quickFileRef.current?.click(), 0);
  };
  const handleQuickUpload = async (file) => {
    if (!file || !quickUploadId) return;
    const product = products.find(p => p.id === quickUploadId);
    if (!product) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await adminApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const path = res.data.url; // relative /api/files/...
      const newImages = [path, ...(product.images || []).filter(u => u !== path)];
      await adminUpdateProduct(product.id, {
        name: product.name, slug: product.slug, category: product.category,
        price: product.price, original_price: product.original_price,
        discount_percent: product.discount_percent || 0,
        short_desc: product.short_desc || "", description: product.description || "",
        images: newImages, tags: product.tags || [],
        stock: product.stock || 0, is_featured: !!product.is_featured,
      });
      toast.success(`Photo updated for ${product.name}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setQuickUploadId(null);
      if (quickFileRef.current) quickFileRef.current.value = "";
    }
  };

  const CATEGORIES = categories.filter(c => c.id !== "all").map(c => c.id);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-24" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl text-[#2D0A22]">Admin Dashboard</h1>
          <p className="text-sm text-[#8E6377]">Manage products, categories & orders</p>
        </div>
        <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#F4D8E2] text-[#8E6377] hover:text-[#D81B60] text-sm">
          <LogOut size={16} /> Logout
        </button>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto hide-scrollbar">
        {[
          { id: "products", label: "Products", icon: Package, count: products.length },
          { id: "categories", label: "Categories", icon: Grid, count: categories.length },
          { id: "hero", label: "Home Hero", icon: Home, count: heroImages.length },
          { id: "reviews", label: "Reviews", icon: Star, count: reviews.length },
          { id: "orders", label: "Orders", icon: ShoppingBag, count: orders.length },
        ].map(t => {
          const I = t.icon;
          return (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 whitespace-nowrap ${tab === t.id ? "bg-[#D81B60] text-white" : "bg-white text-[#2D0A22] border border-[#F4D8E2]"}`}>
              <I size={14} /> {t.label} ({t.count})
            </button>
          );
        })}
      </div>

      {tab === "products" && (
        <>
          <input ref={quickFileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleQuickUpload(e.target.files?.[0])} data-testid="quick-photo-file-input" />
          {CATEGORIES.map(cat => {
            const catProducts = products.filter(p => p.category === cat);
            return (
              <div key={cat} className="mb-8" data-testid={`admin-cat-${cat}`}>
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="font-serif text-xl text-[#2D0A22]">{catLabel(cat)}</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#FCE4EC] text-[#D81B60] font-semibold">{catProducts.length}</span>
                  <div className="flex-1 h-px bg-[#F4D8E2]" />
                  <button
                    data-testid={`add-to-${cat}`}
                    onClick={() => openNew(cat)}
                    className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#D81B60] text-white font-semibold hover:bg-[#AD1457]"
                  >
                    <Plus size={12} /> Add to {catLabel(cat)}
                  </button>
                </div>
                {catProducts.length === 0 ? (
                  <p className="text-sm text-[#8E6377] italic px-2">No products yet — click "Add to {catLabel(cat)}" above.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {catProducts.map(p => (
                      <div key={p.id} data-testid={`admin-product-${p.slug}`} className="bg-white rounded-2xl p-3 border border-[#F4D8E2] flex gap-3">
                        <button
                          type="button"
                          data-testid={`quick-photo-${p.slug}`}
                          onClick={() => quickChangeCover(p)}
                          className="relative group/photo w-20 h-20 rounded-xl overflow-hidden bg-[#FDF2F5] flex-shrink-0"
                          title="Click to change cover photo"
                        >
                          <SmartImage src={p.images?.[0]} alt={p.name} width={160} widths={[80, 120, 160, 240]} sizes="80px" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/photo:opacity-100 transition-opacity flex flex-col items-center justify-center text-white">
                            <Upload size={16} />
                            <span className="text-[9px] font-semibold mt-0.5">Change</span>
                          </div>
                        </button>
                        <div className="flex-1">
                          <p className="font-serif text-lg text-[#2D0A22]">{p.name}</p>
                          <p className="text-xs text-[#8E6377]">₹{p.price} · stock {p.stock} · {p.images?.length || 0} img{(p.images?.length || 0) !== 1 ? "s" : ""}</p>
                          <div className="mt-2 flex gap-2">
                            <button data-testid={`edit-${p.slug}`} onClick={() => openEdit(p)} className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-[#FCE4EC] text-[#D81B60]"><Edit2 size={12} /> Edit</button>
                            <button data-testid={`delete-${p.slug}`} onClick={() => remove(p)} className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-red-50 text-red-600"><Trash2 size={12} /> Delete</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {tab === "categories" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="categories-grid">
          {categories.map(cat => (
            <div key={cat.id} data-testid={`cat-card-${cat.id}`} className="bg-white rounded-2xl p-4 border border-[#F4D8E2] text-center">
              <div className="w-24 h-24 mx-auto rounded-full overflow-hidden mb-3 border-2 border-[#F4D8E2]">
                <SmartImage src={cat.image} alt={cat.name} width={192} widths={[96, 144, 192]} sizes="96px" className="w-full h-full object-cover" />
              </div>
              <p className="font-serif text-lg text-[#2D0A22]">{cat.name}</p>
              {cat.coming_soon && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">COMING SOON</span>}
              <button data-testid={`edit-cat-${cat.id}`} onClick={() => openCatEdit(cat)} className="mt-3 w-full text-xs flex items-center justify-center gap-1 px-2 py-1.5 rounded-full bg-[#FCE4EC] text-[#D81B60] font-semibold hover:bg-[#F8BBD0]">
                <Edit2 size={12} /> Edit
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === "hero" && (
        <div data-testid="hero-manager">
          <div className="bg-white rounded-2xl p-4 border border-[#F4D8E2] mb-4">
            <p className="font-serif text-lg text-[#2D0A22]">Home Page Hero Carousel</p>
            <p className="text-xs text-[#8E6377] mt-1">These images rotate every 5 seconds on the home page. Drag to reorder later — for now, remove & re-add.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {heroImages.map((src, i) => (
              <div key={i} data-testid={`hero-img-${i}`} className="relative group aspect-[4/5] rounded-2xl overflow-hidden border border-[#F4D8E2]">
                <SmartImage src={src} alt="" width={400} widths={[240, 400, 600]} sizes="(max-width:768px) 50vw, 25vw" aspectRatio="4/5" className="w-full h-full object-cover" />
                <button data-testid={`remove-hero-${i}`} onClick={() => removeHero(i)} className="absolute top-2 right-2 w-7 h-7 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center">✕</button>
                <span className="absolute bottom-2 left-2 text-[10px] bg-black/60 text-white px-2 py-0.5 rounded">#{i + 1}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input ref={heroFileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadHeroImage(e.target.files?.[0])} data-testid="hero-file-input" />
            <button data-testid="upload-hero-button" onClick={() => heroFileRef.current?.click()} disabled={heroUploading} className="flex-1 py-3 rounded-xl bg-[#FCE4EC] text-[#D81B60] font-semibold flex items-center justify-center gap-2">
              <Upload size={16} /> {heroUploading ? "Uploading..." : "Upload Hero Image"}
            </button>
            <button data-testid="add-hero-url-button" onClick={addHeroUrl} className="flex-1 py-3 rounded-xl border border-[#F4D8E2] text-[#2D0A22] font-semibold flex items-center justify-center gap-2">
              <ImageIcon size={16} /> Paste URL
            </button>
          </div>
        </div>
      )}

      {tab === "reviews" && (
        <div data-testid="reviews-manager">
          <button data-testid="new-review-button" onClick={openReviewNew} className="mb-4 pill-btn inline-flex items-center gap-2">
            <Plus size={16} /> New Review
          </button>
          <div className="space-y-3">
            {reviews.length === 0 && <p className="text-[#8E6377]">No reviews yet.</p>}
            {reviews.map(rv => (
              <div key={rv.id} data-testid={`admin-review-${rv.id}`} className="bg-white rounded-2xl p-4 border border-[#F4D8E2]">
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <p className="font-semibold text-[#2D0A22]">{rv.name} · {"★".repeat(rv.rating)}{"☆".repeat(5 - rv.rating)}</p>
                    <p className="text-xs text-[#8E6377]">{new Date(rv.created_at).toLocaleString("en-IN")}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${rv.approved ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {rv.approved ? "PUBLISHED" : "HIDDEN"}
                  </span>
                </div>
                <p className="text-sm text-[#2D0A22] mt-2">{rv.text}</p>
                <div className="mt-2 flex gap-2">
                  <button onClick={() => openReviewEdit(rv)} className="text-xs px-2 py-1 rounded bg-[#FCE4EC] text-[#D81B60] flex items-center gap-1"><Edit2 size={12} /> Edit</button>
                  <button onClick={() => removeReview(rv)} className="text-xs px-2 py-1 rounded bg-red-50 text-red-600 flex items-center gap-1"><Trash2 size={12} /> Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-3">
          {orders.length === 0 && (
            <div className="text-center py-12 rounded-3xl bg-white border border-[#F4D8E2]">
              <div className="text-4xl">📦</div>
              <p className="font-serif text-lg text-[#2D0A22] mt-2">No orders yet</p>
              <p className="text-sm text-[#8E6377]">Orders placed by customers will appear here.</p>
            </div>
          )}
          {orders.map(o => {
            const meta = STATUS_META[o.status] || STATUS_META.pending_whatsapp;
            return (
              <div key={o.id} data-testid={`admin-order-${o.id}`} className="bg-white rounded-2xl p-5 border border-[#F4D8E2] shadow-[0_4px_20px_rgba(216,27,96,0.04)]">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-serif text-lg text-[#2D0A22]">{o.customer_name}</p>
                    <p className="text-xs text-[#8E6377]">📞 {o.phone}</p>
                    <p className="text-xs text-[#8E6377] mt-1">📍 {o.address}</p>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full font-semibold ${meta.color}`}>{meta.label}</span>
                </div>
                <div className="mt-3 pt-3 border-t border-[#F4D8E2] space-y-1">
                  {o.items?.map((it, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-[#2D0A22]">{it.name} × {it.quantity}</span>
                      <span className="text-[#8E6377]">₹{(it.price * it.quantity).toLocaleString("en-IN")}</span>
                    </div>
                  ))}
                </div>
                <p className="text-right font-bold text-[#D81B60] mt-2">Total ₹{o.total?.toLocaleString("en-IN")}</p>

                {/* Delhivery shipment block */}
                <div className="mt-3 p-3 rounded-xl bg-[#FDF2F5] border border-[#F4D8E2] text-xs">
                  {o.awb ? (
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="font-semibold text-[#2D0A22]">
                          🚚 Delhivery AWB: <span className="font-mono text-[#D81B60]">{o.awb}</span>
                        </div>
                        <div className="text-[#8E6377] mt-0.5">Status: {o.shipment_status || "manifested"}</div>
                        {o.tracking_url && (
                          <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-[#D81B60] hover:underline text-[11px]">Track on Delhivery →</a>
                        )}
                      </div>
                      <button
                        data-testid={`sync-shipment-${o.id}`}
                        onClick={async () => {
                          try { const r = await adminSyncShipment(o.id); toast.success(`Synced: ${r.status}`); refresh(); }
                          catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
                        }}
                        className="text-[11px] px-3 py-1.5 rounded-full bg-white border border-[#F4D8E2] text-[#D81B60] font-semibold hover:bg-[#FCE4EC]"
                      >🔄 Sync status</button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[#8E6377]">No Delhivery shipment yet.</span>
                      <button
                        data-testid={`ready-to-dispatch-${o.id}`}
                        onClick={async () => {
                          if (!window.confirm("Create Delhivery shipment and request pickup?")) return;
                          try {
                            const r = await adminCreateShipment(o.id);
                            toast.success(`AWB ${r.awb} — pickup scheduled`);
                            refresh();
                          } catch (e) {
                            const d = e?.response?.data?.detail;
                            // Surface the real Delhivery reason when possible
                            const dhlv = d?.response?.rmk || d?.response?.error?.[0] || (typeof d === "string" ? d : null);
                            toast.error(dhlv || "Shipment failed — check server logs");
                          }
                        }}
                        className="text-[11px] px-3 py-1.5 rounded-full bg-[#D81B60] text-white font-semibold hover:bg-[#AD1457]"
                      >🚀 Ready to Dispatch</button>
                    </div>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap gap-2 items-center">
                  {["accepted", "shipped", "delivered", "cancelled"].map(s => (
                    <button key={s} data-testid={`order-status-${o.id}-${s}`} disabled={o.status === s}
                      onClick={async () => { try { await adminUpdateOrderStatus(o.id, s); toast.success(`Marked ${s}`); refresh(); } catch { toast.error("Update failed"); } }}
                      className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${o.status === s ? "bg-[#D81B60] text-white" : "bg-[#FCE4EC] text-[#D81B60] hover:bg-[#F8BBD0]"}`}>
                      {s === "accepted" ? "✓ Accept" : s === "shipped" ? "📦 Ship" : s === "delivered" ? "🎉 Deliver" : "✕ Cancel"}
                    </button>
                  ))}
                  {!o.awb && (
                    <button
                      data-testid={`delete-order-${o.id}`}
                      onClick={async () => {
                        if (!window.confirm(`Delete order from ${o.customer_name}? This cannot be undone.`)) return;
                        try { await adminDeleteOrder(o.id); toast.success("Order deleted"); refresh(); }
                        catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
                      }}
                      className="ml-auto text-xs px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 hover:bg-rose-100 font-semibold border border-rose-200"
                    >🗑 Delete</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Review edit modal */}
      {editingReview !== null && (
        <div className="fixed inset-0 bg-[#2D0A22]/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="review-edit-modal">
          <div className="bg-white rounded-3xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-2xl">{editingReview?.id ? "Edit" : "New"} Review</h3>
              <button onClick={() => setEditingReview(null)}><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="review-name-input" placeholder="Customer name" value={reviewForm.name} onChange={e => setReviewForm({...reviewForm, name: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-[#F4D8E2]" />
              <div className="flex gap-1">
                {[1,2,3,4,5].map(n => (
                  <button key={n} data-testid={`admin-star-${n}`} type="button" onClick={() => setReviewForm({...reviewForm, rating: n})}>
                    <Star size={28} className={n <= reviewForm.rating ? "text-[#F59E0B] fill-[#F59E0B]" : "text-[#E5D3DA]"} />
                  </button>
                ))}
              </div>
              <textarea data-testid="review-text-input" rows={4} maxLength={500} placeholder="Review text" value={reviewForm.text} onChange={e => setReviewForm({...reviewForm, text: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-[#F4D8E2] resize-none" />
              <label className="flex items-center gap-2 text-sm px-3 py-2 rounded-xl border border-[#F4D8E2]">
                <input type="checkbox" checked={reviewForm.approved} onChange={e => setReviewForm({...reviewForm, approved: e.target.checked})} /> Show on website
              </label>
              <button data-testid="save-review-button" onClick={saveReview} className="w-full pill-btn">Save Review</button>
            </div>
          </div>
        </div>
      )}

      {/* Product edit modal */}
      {editing !== null && (
        <div className="fixed inset-0 bg-[#2D0A22]/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="product-edit-modal">
          <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-2xl">{editing?.id ? "Edit" : "New"} Product</h3>
              <button data-testid="close-modal-button" onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="form-name" placeholder="Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-[#F4D8E2]" />
              <input data-testid="form-slug" placeholder="Slug (unique url handle)" value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-[#F4D8E2]" />
              <select data-testid="form-category" value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-[#F4D8E2]">
                {CATEGORIES.map(c => <option key={c} value={c}>{catLabel(c)}</option>)}
              </select>
              <div className="grid grid-cols-3 gap-2">
                <input data-testid="form-price" type="number" placeholder="Price" value={form.price} onChange={e => setForm({...form, price: e.target.value})} className="px-3 py-2 rounded-xl border border-[#F4D8E2]" />
                <input type="number" placeholder="Original" value={form.original_price} onChange={e => setForm({...form, original_price: e.target.value})} className="px-3 py-2 rounded-xl border border-[#F4D8E2]" />
                <input type="number" placeholder="% Off" value={form.discount_percent} onChange={e => setForm({...form, discount_percent: e.target.value})} className="px-3 py-2 rounded-xl border border-[#F4D8E2]" />
              </div>
              <input placeholder="Short description" value={form.short_desc} onChange={e => setForm({...form, short_desc: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-[#F4D8E2]" />
              <textarea placeholder="Description" rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-[#F4D8E2] resize-none" />

              {/* Multi-image */}
              <div>
                <p className="text-xs font-semibold text-[#2D0A22] mb-2">Product Images ({form.images.length})</p>
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {form.images.map((src, i) => (
                    <div key={i} className="relative group aspect-square rounded-xl overflow-hidden border border-[#F4D8E2]">
                      <SmartImage src={src} alt="" width={240} widths={[120, 240, 400]} sizes="120px" aspectRatio="1/1" className="w-full h-full object-cover" />
                      <button data-testid={`remove-img-${i}`} onClick={() => removeImage(i)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs">
                        ✕
                      </button>
                      {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] bg-black/60 text-white px-1.5 py-0.5 rounded">Cover</span>}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="form-image-file" />
                  <button type="button" data-testid="upload-image-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex-1 py-2 rounded-xl bg-[#FCE4EC] text-[#D81B60] font-semibold text-sm flex items-center justify-center gap-2">
                    <Upload size={14} /> {uploading ? "Uploading..." : "Upload Image"}
                  </button>
                  <button type="button" data-testid="add-url-button" onClick={addImageUrl} className="flex-1 py-2 rounded-xl border border-[#F4D8E2] text-[#2D0A22] font-semibold text-sm flex items-center justify-center gap-2">
                    <ImageIcon size={14} /> Paste URL
                  </button>
                </div>
              </div>

              <input placeholder="Tags (comma separated)" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={e => setForm({...form, tags: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-[#F4D8E2]" />
              <div className="grid grid-cols-2 gap-2">
                <input type="number" placeholder="Stock" value={form.stock} onChange={e => setForm({...form, stock: e.target.value})} className="px-3 py-2 rounded-xl border border-[#F4D8E2]" />
                <label className="flex items-center gap-2 text-sm px-3 py-2 rounded-xl border border-[#F4D8E2]">
                  <input type="checkbox" checked={form.is_featured} onChange={e => setForm({...form, is_featured: e.target.checked})} /> Featured
                </label>
              </div>
              <button data-testid="save-product-button" onClick={save} className="w-full pill-btn">Save Product</button>
            </div>
          </div>
        </div>
      )}

      {/* Category edit modal */}
      {editingCat && (
        <div className="fixed inset-0 bg-[#2D0A22]/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="category-edit-modal">
          <div className="bg-white rounded-3xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-2xl">Edit Category</h3>
              <button data-testid="close-cat-modal" onClick={() => setEditingCat(null)}><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="cat-name-input" placeholder="Category name" value={catForm.name} onChange={e => setCatForm({...catForm, name: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-[#F4D8E2]" />
              <div className="flex items-center gap-3">
                <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-[#F4D8E2] flex-shrink-0">
                  {catForm.image ? <SmartImage src={catForm.image} alt="" width={160} widths={[80, 120, 160]} sizes="80px" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#FCE4EC]" />}
                </div>
                <div className="flex-1 space-y-2">
                  <input ref={catFileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadCatImage(e.target.files?.[0])} data-testid="cat-image-file" />
                  <button type="button" data-testid="upload-cat-image" onClick={() => catFileRef.current?.click()} disabled={catUploading} className="w-full py-2 rounded-xl bg-[#FCE4EC] text-[#D81B60] font-semibold text-sm flex items-center justify-center gap-2">
                    <Upload size={14} /> {catUploading ? "Uploading..." : "Upload Icon"}
                  </button>
                  <input placeholder="or paste image URL" value={catForm.image} onChange={e => setCatForm({...catForm, image: e.target.value})} className="w-full px-3 py-1.5 rounded-xl border border-[#F4D8E2] text-xs" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm px-3 py-2 rounded-xl border border-[#F4D8E2]">
                <input type="checkbox" checked={catForm.coming_soon} onChange={e => setCatForm({...catForm, coming_soon: e.target.checked})} /> Mark as "Coming Soon"
              </label>
              <button data-testid="save-cat-button" onClick={saveCat} className="w-full pill-btn">Save Category</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
